import React, { ReactNode } from "react";
import { Form } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import Branding from "../../config/Branding";


type FactorDropdownProperties = {
    parameter: FactorDropdownParameter,
    callback: (paramName: string, value: string) => void,
    value?: string
}

type FactorDropdownState = {
    value: string,
    selectedValues: Array<any>
}

export default class FactorDropdown extends React.Component<FactorDropdownProperties, FactorDropdownState> {

    constructor(props: FactorDropdownProperties) {
        super(props);

        const defaultValue = props.parameter.default as string || (props.parameter.values?.[0] ?? "");

        this.state = {
            value: props.value !== undefined ? props.value : defaultValue,
            selectedValues: []
        };
    }

    public componentDidMount(): void {
        // In controlled mode (value prop provided), do not emit initial default
    }

    public reset(): void {
        const defaultValue = this.props.parameter.default as string || (this.props.parameter.values?.[0] ?? "");
        this.setState({
            value: defaultValue,
            selectedValues: []
        });
    }

    public componentDidUpdate(prevProps: FactorDropdownProperties): void {
        if (prevProps.value !== this.props.value) {
            const isMultiSelect = this.props.parameter.cardinality === "multiple";
            if (isMultiSelect) {
                const incoming = (this.props.value || "").trim();
                const values = incoming.length ? incoming.split(",") : [];
                const selected = values.map(v => ({ value: v, label: v }));
                this.setState({ selectedValues: selected });
            } else if (this.props.value !== undefined) {
                this.setState({ value: this.props.value });
            }
        }
    }

    public handleOnChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.setState({
            value: event.target.value
        });
        this.props.callback(this.props.parameter.name, event.target.value);
    }

    public handleMultiSelectChange = (selected: Array<any>): void => {
        this.setState({
            selectedValues: selected
        });

        if (selected.length > 0) {
            // Send comma-separated values like filters do
            const values = selected.map((item: any) => item.value || item);
            this.props.callback(this.props.parameter.name, values.join(","));
        } else {
            this.props.callback(this.props.parameter.name, "");
        }
    }

    public render(): ReactNode {
        const { parameter } = this.props;
        const isMultiSelect = parameter.cardinality === "multiple";

        if (isMultiSelect) {
            // Use Typeahead for multi-select parameters
            const options = parameter.values?.map(value => ({
                value: value,
                label: value
            })) || [];

            return (
                <Typeahead
                    id={parameter.name}
                    multiple={true}
                    options={options}
                    selected={this.state.selectedValues}
                    placeholder={`Select ${parameter.label || parameter.name}...`}
                    onChange={(selected: any) => this.handleMultiSelectChange(selected)}
                    clearButton
                />
            );
        } else {
            // Use regular dropdown for single-select parameters
            return (
                <Form.Control
                    as="select"
                    id={parameter.name}
                    title={parameter.name}
                    value={this.state.value}
                    size="sm"
                    style={{ color: Branding.white, backgroundColor: "#1a1a1a", borderColor: "#333" }}
                    onChange={this.handleOnChange}>

                    {parameter.values?.map((value, i) => {
                        return (
                            <option value={value} key={i}>
                                {value}
                            </option>
                        );
                    }) || null}
                </Form.Control>
            );
        }
    }
}
